import { useQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import {
  addDays,
  eatDayOf,
  formatDay,
  formatEatTime,
  formatIsoDay,
  parseIsoDay,
} from '@wodi/domain';
import type { TheatreListCase } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  EmptyState,
  formatShortAge,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';

import { theatreListQuery } from '../api/theatre.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from './theatre.module.css';
import { errorText, progressText, STATUS_TAGS, URGENCY_WORDS } from './words.ts';

// Theatre: one day's list, every active theatre with its cases in time order
// (docs/plans/phase2-theatre.md §6). Cases are booked from the patient's stay or visit.

const route = getRouteApi('/_app/theatre');

function patientText(c: TheatreListCase): string {
  if (c.patient === null) return 'Restricted record';
  return `${c.patient.displayName}, ${c.patient.sex} ${formatShortAge(c.patient.age, false)}`;
}

function slotText(c: TheatreListCase): string {
  const start = new Date(c.scheduledStart);
  const end = new Date(start.getTime() + c.expectedMinutes * 60_000);
  return `${formatEatTime(start)} to ${formatEatTime(end)}`;
}

export function TheatreListPage() {
  const { date } = route.useSearch();
  const navigate = useNavigate();
  const online = useOnline();
  const list = useQuery(theatreListQuery(date ?? null));
  const today = formatIsoDay(eatDayOf(new Date()));
  const shown = date ?? list.data?.date ?? today;
  const parsed = parseIsoDay(shown);
  const go = (day: string | null) => {
    void navigate({ to: '/theatre', search: day === null || day === today ? {} : { date: day } });
  };
  const shift = (days: number) => {
    if (parsed.ok) go(formatIsoDay(addDays(parsed.value, days)));
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Theatre"
        description={`The theatre list for ${parsed.ok ? formatDay(parsed.value) : shown}. Cases are booked from the patient's stay or visit.`}
      />
      <div className={styles.dayNav}>
        <Button
          onPress={() => {
            shift(-1);
          }}
        >
          Previous day
        </Button>
        <Button
          isDisabled={shown === today}
          onPress={() => {
            go(null);
          }}
        >
          Today
        </Button>
        <Button
          onPress={() => {
            shift(1);
          }}
        >
          Next day
        </Button>
        <DateField
          label="Day"
          value={shown}
          onChange={(value) => {
            if (value !== null) go(value);
          }}
        />
      </div>
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. The list shows what was last loaded and refreshes when the
          connection is back.
        </Notice>
      ) : null}
      {list.isPending ? (
        <LoadingBlock label="Loading the theatre list" rows={6} />
      ) : list.isError ? (
        <Notice
          tone="error"
          action={<Button onPress={() => void list.refetch()}>Try again</Button>}
        >
          {errorText(list.error, "The theatre list couldn't be loaded. Try again.")}
        </Notice>
      ) : list.data.theatres.length === 0 ? (
        <EmptyState>
          No theatres set up yet. An administrator adds them in Settings, Theatres.
        </EmptyState>
      ) : (
        list.data.theatres.map((theatre) => (
          <Section
            key={theatre.id}
            title={theatre.name}
            {...(theatre.cases.length === 0
              ? {}
              : {
                  description: `${String(theatre.cases.filter((c) => c.status !== 'cancelled').length)} cases`,
                })}
          >
            <Table flat dense aria-label={`Cases in ${theatre.name}`}>
              <TableHeader>
                <Column isRowHeader>Time</Column>
                <Column>Patient</Column>
                <Column>Procedure</Column>
                <Column>Surgeon</Column>
                <Column>Status</Column>
                <Column>Checklist</Column>
              </TableHeader>
              <TableBody
                items={theatre.cases}
                renderEmptyState={() => 'No cases booked for this day.'}
              >
                {(c) => (
                  <Row id={c.id}>
                    <Cell>
                      <Link
                        to="/theatre/cases/$caseId"
                        params={{ caseId: c.id }}
                        className={styles.time}
                      >
                        {slotText(c)}
                      </Link>
                    </Cell>
                    <Cell>
                      {patientText(c)}
                      {c.patient === null ? null : (
                        <span
                          className={`wodi-id ${styles.meta}`}
                        >{` ${c.patient.patientNumber}`}</span>
                      )}
                    </Cell>
                    <Cell>
                      {c.procedureName ?? 'Not shown'}
                      {c.urgency === 'emergency' ? (
                        <>
                          {' '}
                          <Tag state="attention">{URGENCY_WORDS.emergency}</Tag>
                        </>
                      ) : null}
                    </Cell>
                    <Cell>{c.surgeon.displayName}</Cell>
                    <Cell>
                      <Tag state={STATUS_TAGS[c.status][1]}>{STATUS_TAGS[c.status][0]}</Tag>
                    </Cell>
                    <Cell>{progressText(c)}</Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </Section>
        ))
      )}
    </div>
  );
}
